import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Languages, ChevronRight, Users } from "lucide-react";
import { ColorDot, colorLabel } from "./ProgressBar";
import { fmtDate } from "./ChapterRow";

function GroupLevelRow({ g }) {
  const inner = (
    <>
      <div className="col-span-6 sm:col-span-2 flex items-center gap-2">
        <span className="font-mono-spec font-bold text-[var(--maroon)]">{g.level}</span>
        {!g.teacher && (
          <span className="px-1.5 py-0.5 bg-[var(--just)]/15 text-[var(--just)] text-[10px]" data-testid={`group-no-teacher-${g.group_class_id}`}>
            Non affecté
          </span>
        )}
      </div>
      <div className="col-span-6 sm:col-span-3 text-sm">{g.teacher?.name || "—"}</div>
      <div className="col-span-6 sm:col-span-3 text-xs text-[var(--ink-soft)]">
        <span className="inline-flex items-center gap-1"><Users size={12} /> {g.student_count} élève{g.student_count > 1 ? "s" : ""}</span>
        {g.class_names.length > 0 && <span className="ml-2">({g.class_names.join(" / ")})</span>}
      </div>
      <div className="col-span-6 sm:col-span-3 text-xs">
        {g.current_chapter ? (
          <span>
            {g.current_chapter.title}
            {g.current_chapter.end_date && <span className="text-[var(--muted)] font-mono-spec"> · fin prévue {fmtDate(g.current_chapter.end_date)}</span>}
          </span>
        ) : (
          <span className="text-[var(--muted)] italic">Aucun chapitre en cours</span>
        )}
      </div>
      <div className="col-span-5 sm:col-span-1 flex items-center justify-end gap-2 text-xs">
        <ColorDot color={g.progress.color} />
        <span className="font-mono-spec">{g.progress.percent}%</span>
        <span className="sr-only">{colorLabel(g.progress.color)}</span>
        {g.assignment_id && <ChevronRight size={14} className="text-[var(--muted)]" />}
      </div>
    </>
  );
  const cls = "grid grid-cols-12 gap-2 items-center px-4 py-3 border-b border-[var(--parchment)] last:border-0";
  if (g.assignment_id) {
    return (
      <Link to={`/admin/assignment/${g.assignment_id}`} className={`${cls} hover:bg-[var(--parchment)]/40 transition-colors`} data-testid={`language-group-row-${g.group_class_id}`}>
        {inner}
      </Link>
    );
  }
  return <div className={cls} data-testid={`language-group-row-${g.group_class_id}`}>{inner}</div>;
}

export default function LanguageGroupsView() {
  const [groups, setGroups] = useState(null);

  useEffect(() => {
    api.get("/admin/language-groups").then(({ data }) => setGroups(data));
  }, []);

  if (!groups) return null;

  return (
    <div className="mt-10" data-testid="language-groups-view">
      <div className="eyebrow flex items-center gap-2"><Languages size={14} /> Groupes de langue par niveau</div>
      <div className="mt-3 grid lg:grid-cols-1 gap-4">
        {groups.map((sub) => (
          <section key={sub.subject.id} className="bg-white border border-[var(--parchment-2)]" data-testid={`language-groups-${sub.subject.code}`}>
            <header className="px-4 py-3 border-b border-[var(--parchment-2)] flex items-baseline justify-between">
              <h3 className="font-serif-display text-xl font-semibold">{sub.subject.name}</h3>
              <span className="text-xs text-[var(--muted)]">{sub.levels.filter((l) => l.teacher).length}/{sub.levels.length} niveaux affectés</span>
            </header>
            {sub.levels.map((g) => <GroupLevelRow key={g.group_class_id} g={g} />)}
          </section>
        ))}
      </div>
    </div>
  );
}
